import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";

interface ExclusionRules {
	id: string;
	doubleHeaderNames: Array<string>;
	excludedCities: Array<string>;
	excludedVenues: Array<string>;
	excludedHeadliners: Array<string>;
	eventMappingMatchThreshold: number;
	eventMappingMaxResults: number;
	eventMappingFuzzyLogicCutOff: number;
	fuzzyLogicDateTimeVenueMatchThreshold: number;
	fuzzyLogicDateTimeCityMatchThreshold: number;
	enabledFuzzyLogicOptions: Array<string>;
}

export interface ExclusionRulesState {
	rules: ExclusionRules | null;
	loading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
}

const initialState: ExclusionRulesState = {
	rules: null,
	loading: "idle",
	error: null,
};

export const fetchExclusionRules = createAsyncThunk(
	"exclusionRules/fetchExclusionRules",
	async (_, { rejectWithValue }) => {
		try {
			const token = sessionStorage.getItem("token");
			const headers = {
				Authorization: `Bearer ${token}`,
			};
			const response = await axios.get(`${getBackendUrl()}/exclusion-rule`, { headers });
			return response.data;
		} catch (error) {
			return rejectWithValue("Failed to fetch exclusion rules");
		}
	},
);

export const saveExclusionRules = createAsyncThunk(
	"exclusionRules/saveExclusionRules",
	async (
		updatedRules: {
			doubleHeaderNames: Array<string>;
			excludedCities: Array<string>;
			excludedHeadliners: Array<string>;
			excludedVenues: Array<string>;
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			const token = sessionStorage.getItem("token");
			const headers = {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			};

			// Make POST request with the full exclusion rules object
			const response = await axios.post(
				`${getBackendUrl()}/exclusion-rule`,
				updatedRules, // Send the full object
				{ headers }
			);

			// Fetch updated exclusion rules after saving
			dispatch(fetchExclusionRules());

			return response.data;
		} catch (error) {
			return rejectWithValue("Failed to save exclusion rules");
		}
	}
);


export const exclusionRulesSlice = createSlice({
	name: "exclusionRules",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchExclusionRules.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(fetchExclusionRules.fulfilled, (state, action) => {
				state.loading = "succeeded";
				state.rules = action.payload;
			})
			.addCase(fetchExclusionRules.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			});
	},
});

export default exclusionRulesSlice.reducer;